import { POSTS_API } from "@/lib/apis";
import { cn } from "@/lib/utils";
import { useAuth } from "@/providers/auth-provider";
import { useModal } from "@/providers/modal-provider";
import { ModalType } from "@/types/modal-type";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ThumbsUpIcon } from "lucide-react";
import { useState } from "react";
import toast from "react-hot-toast";

type TPostCommentLikeButtonProps = {
	commentSlug: string;
	postSlug: string;
	likesCount: number;
	isLiked?: boolean;
};

const PostCommentLikeButton = ({
	commentSlug,
	postSlug,
	likesCount,
	isLiked = false,
}: TPostCommentLikeButtonProps) => {
	const { isLoggedIn } = useAuth();
	const { openModal } = useModal();
	const queryClient = useQueryClient();

	const [liked, setLiked] = useState<boolean>(isLiked);
	const [count, setCount] = useState<number>(likesCount);

	const handleLikeAction = async () => {
		if (!isLoggedIn) {
			openModal(ModalType.Auth);
			return;
		}

		const url = new URL(`${POSTS_API}/${postSlug}/comments/reactions`);

		await fetch(url, { method: "PUT", body: JSON.stringify({ commentSlug }) });
	};

	const likePostCommentMutation = useMutation<void, Error>({
		mutationFn: handleLikeAction,
		onMutate: () => {
			if (liked) {
				setCount(count - 1);
			} else {
				setCount(count + 1);
			}

			setLiked((prev) => !prev);
		},
		onSuccess: () => {
			return queryClient.invalidateQueries({ queryKey: ["post-comments", postSlug] });
		},
		onError: (error) => {
			toast.error(error.message);
		},
	});

	return (
		<button
			type="button"
			onClick={() => likePostCommentMutation.mutate()}
			className={cn(
				"flex items-center justify-start gap-1 rounded-full border px-4 py-2 text-xs",
				liked ? "border-brand-primary text-brand-primary" : "border-gray-500 text-gray-500"
			)}
		>
			<span>
				<ThumbsUpIcon className="h-4 w-4" />
			</span>

			<span>{count} Likes</span>
		</button>
	);
};

export default PostCommentLikeButton;
